import { useMutation, useQuery } from '@apollo/client/react';
import { useOktaAuth } from '@okta/okta-react';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { INSERT_MILESTONE } from '@optools/services/operator-gateway/milestones/mutations';
import {
  GET_MILESTONE_SCHEMAS,
  GET_MILESTONES,
} from '@optools/services/operator-gateway/milestones/queries';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { MilestoneForm, MilestoneFormValues } from './milestone-form';

export interface AddMilestoneDialogProps {
  mediaId: string;
  programDateTime?: string;
}

export function AddMilestoneDialog({ mediaId, programDateTime }: AddMilestoneDialogProps) {
  const { authState } = useOktaAuth();
  const { data } = useQuery(GET_MILESTONE_SCHEMAS);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const milestoneSchemas = useMemo(() => data?.milestoneSchemas ?? [], [data]);

  const [insertMilestone] = useMutation(INSERT_MILESTONE, {
    onCompleted: (data) => {
      const success = !!data.insertMilestone;
      if (!success) {
        toast.error('Insert Milestone Failure');
        return;
      }
      toast.success('Insert Milestone Success');
      setIsOpen(false);
    },
    refetchQueries: [{ query: GET_MILESTONES, variables: { mediaId } }],
  });

  const onSubmit = (value: MilestoneFormValues) => {
    insertMilestone({
      variables: {
        mediaId,
        milestoneInput: {
          ...value,
          updatedBy: authState?.accessToken?.claims?.sub ?? 'SOP Anon',
        },
      },
    });
    setIsOpen(false);
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(value: boolean) => {
        setIsOpen(value);
      }}>
      <DialogTrigger asChild>
        <DataTableAddButton>Add Milestone</DataTableAddButton>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Milestone</DialogTitle>
          <DialogDescription className="sr-only">
            Adds a new milestone to the game.
          </DialogDescription>
        </DialogHeader>
        <MilestoneForm
          milestoneSchemas={milestoneSchemas}
          programDateTime={programDateTime}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
